import { useState, useEffect } from 'react';
import { Icon } from './Icon';
import { api } from '../../utils/api';

interface AiSuggestButtonProps {
  type: 'risk-treatment' | 'soa-justification' | 'soa-implementation';
  context: Record<string, string>;
  onSuggestion: (text: string) => void;
  label?: string;
  className?: string;
}

export function AiSuggestButton({
  type,
  context,
  onSuggestion,
  label = 'AI Suggest',
  className = '',
}: AiSuggestButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // Close on Escape key
  useEffect(() => {
    if (!showPreview) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowPreview(false);
        setSuggestion('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showPreview]);

  const handleClick = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.post('/ai/suggest', { type, context }) as { suggestion: string };
      if (result.suggestion) {
        setSuggestion(result.suggestion);
        setShowPreview(true);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'AI suggestion failed';
      setError(message);
      setTimeout(() => setError(''), 4000);
    } finally {
      setLoading(false);
    }
  };

  const handleUseSuggestion = () => {
    onSuggestion(suggestion);
    setShowPreview(false);
    setSuggestion('');
  };

  const handleDismiss = () => {
    setShowPreview(false);
    setSuggestion('');
  };

  return (
    <>
      <div className="relative inline-flex">
        <button
          type="button"
          onClick={handleClick}
          disabled={loading}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
            loading
              ? 'bg-purple-100 text-purple-400 cursor-wait'
              : 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 shadow-sm hover:shadow'
          } ${className}`}
          title="Generate AI suggestion"
        >
          {loading ? (
            <Icon name="Loader" size={14} className="animate-spin" />
          ) : (
            <Icon name="Sparkles" size={14} />
          )}
          {loading ? 'Generating...' : label}
        </button>
        {error && (
          <div className="absolute top-full left-0 mt-1 z-10 px-3 py-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg whitespace-nowrap">
            {error}
          </div>
        )}
      </div>

      {/* AI Suggestion Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleDismiss}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-purple-500 to-indigo-500">
              <div className="flex items-center gap-2 text-white">
                <Icon name="Sparkles" size={18} />
                <h3 className="text-base font-semibold">AI Suggestion</h3>
              </div>
              <button
                type="button"
                onClick={handleDismiss}
                className="text-white/80 hover:text-white transition-colors"
                title="Dismiss"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Suggestion text */}
            <div className="px-5 py-4">
              <p className="text-sm text-gray-500 mb-2">Review the suggestion below. Click <strong>Use Suggestion</strong> to apply it, or <strong>Dismiss</strong> to discard.</p>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-sm text-gray-800 leading-relaxed whitespace-pre-wrap max-h-60 overflow-y-auto">
                {suggestion}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 px-5 py-4 bg-gray-50 border-t border-gray-200">
              <button
                type="button"
                onClick={handleDismiss}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Dismiss
              </button>
              <button
                type="button"
                onClick={handleUseSuggestion}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-indigo-500 rounded-lg hover:from-purple-600 hover:to-indigo-600 shadow-sm hover:shadow transition-all"
              >
                <Icon name="CheckCircle" size={16} />
                Use Suggestion
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
